import type { MobileArtifactLocation, MobileFrontEndMapView } from '@/shared/mobile-service/api/cors-mobile-api';
import { resolvePath } from './message-mapping';

/** A single artifact row resolved to a plottable coordinate. */
export interface MapPoint {
  id: string;
  latitude: number;
  longitude: number;
  label: string | null;
  timestamp: string | null;
  /** Original row, for the detail popover. */
  row: Record<string, unknown>;
}

export function toCoordinate(value: unknown): number | null {
  if (value == null) return null;
  const num = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(num)) return null;
  return num;
}

function asLabel(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'object') return null;
  return String(value);
}

function inRange(latitude: number, longitude: number): boolean {
  return latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
}

/**
 * Turn hydrate (or list) rows into plottable points using the authored map view.
 *
 * Nested paths such as ``position.latitude`` are resolved; a row without a finite
 * in-range pair is dropped rather than plotted at (0, 0).
 */
const LABEL_FIELD_CANDIDATES = ['label', 'locationName', 'address', 'name', 'ssid'] as const;
const TIMESTAMP_FIELD_CANDIDATES = [
  'timestamp',
  'timeStamp',
  'createdDateTime',
  'createdAt',
  'startDateTime',
  'endDateTime',
  'vicinityEntryDateTime',
] as const;

function inferLatLon(row: Record<string, unknown>): { latitude: number; longitude: number } | null {
  const pairs: Array<[unknown, unknown]> = [
    [resolvePath(row, 'latitude'), resolvePath(row, 'longitude')],
    [resolvePath(row, 'position.latitude'), resolvePath(row, 'position.longitude')],
  ];
  const payload = resolvePath(row, 'providerPayload.Position');
  if (Array.isArray(payload) && payload.length >= 2) {
    pairs.push([payload[1], payload[0]]);
  }
  for (const [rawLat, rawLon] of pairs) {
    const latitude = toCoordinate(rawLat);
    const longitude = toCoordinate(rawLon);
    if (latitude == null || longitude == null || !inRange(latitude, longitude)) continue;
    return { latitude, longitude };
  }
  return null;
}

/**
 * Copy rows and fill top-level ``latitude`` / ``longitude`` when the pair lives
 * under ``position`` or a GeoJSON-style ``providerPayload.Position``.
 */
export function rowsWithInferredCoordinates(
  rows: Record<string, unknown>[],
): Record<string, unknown>[] {
  return rows.map((row) => {
    const existingLat = toCoordinate(resolvePath(row, 'latitude'));
    const existingLon = toCoordinate(resolvePath(row, 'longitude'));
    if (existingLat != null && existingLon != null && inRange(existingLat, existingLon)) {
      return row;
    }
    const inferred = inferLatLon(row);
    if (!inferred) return row;
    return { ...row, latitude: inferred.latitude, longitude: inferred.longitude };
  });
}

function inferLabelField(row: Record<string, unknown>): string | undefined {
  for (const key of LABEL_FIELD_CANDIDATES) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) return key;
  }
  return undefined;
}

function inferTimestampField(row: Record<string, unknown>): string | undefined {
  for (const key of TIMESTAMP_FIELD_CANDIDATES) {
    if (row[key] != null && row[key] !== '') return key;
  }
  return undefined;
}

/**
 * Authored map view, then displayFields, then a lat/lon pair already on the rows.
 *
 * Visits and reverse-geocode places often hydrate with coordinates but no registry
 * map view. The citation pane still needs a view object to plot them.
 */
function locationPairOnRow(
  row: Record<string, unknown>,
  location: MobileArtifactLocation,
): { latitude: number; longitude: number } | null {
  const latitude = toCoordinate(resolvePath(row, location.latitude));
  const longitude = toCoordinate(resolvePath(row, location.longitude));
  if (latitude == null || longitude == null || !inRange(latitude, longitude)) return null;
  return { latitude, longitude };
}

function locationLabel(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '';
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

export function inferCitationMapView(
  authored: MobileFrontEndMapView | undefined,
  displayFields: Record<string, string> | null | undefined,
  rows: Record<string, unknown>[],
  locations?: MobileArtifactLocation[] | null,
): MobileFrontEndMapView | undefined {
  if (authored?.latitude_field && authored?.longitude_field) return authored;
  if (displayFields?.latitude && displayFields?.longitude) {
    return {
      name: 'map',
      latitude_field: displayFields.latitude,
      longitude_field: displayFields.longitude,
      ...(displayFields.label ? { label_field: displayFields.label } : {}),
      ...(displayFields.timestamp ? { timestamp_field: displayFields.timestamp } : {}),
    };
  }
  const named = (locations ?? []).find((location) =>
    rows.some((row) => locationPairOnRow(row, location) != null),
  );
  if (named) {
    const sample = rows.find((row) => inferTimestampField(row) != null);
    const timestampField = sample ? inferTimestampField(sample) : undefined;
    return {
      name: 'map',
      latitude_field: named.latitude,
      longitude_field: named.longitude,
      ...(timestampField ? { timestamp_field: timestampField } : {}),
    };
  }
  const sample = rows.find((row) => inferLatLon(row) != null);
  if (!sample) return undefined;
  const labelField = inferLabelField(sample);
  const timestampField = inferTimestampField(sample);
  return {
    name: 'map',
    latitude_field: 'latitude',
    longitude_field: 'longitude',
    ...(labelField ? { label_field: labelField } : {}),
    ...(timestampField ? { timestamp_field: timestampField } : {}),
  };
}

export function mapRowsToPoints(
  rows: Record<string, unknown>[],
  view: MobileFrontEndMapView,
  idField: string | null,
  idOffset = 0,
  locations?: MobileArtifactLocation[] | null,
): MapPoint[] {
  if ((locations?.length ?? 0) >= 2) {
    return mapTripRowsToPoints(rows, locations ?? [], view, idField, idOffset);
  }
  const points: MapPoint[] = [];
  for (const [index, row] of rows.entries()) {
    const latitude = toCoordinate(resolvePath(row, view.latitude_field));
    const longitude = toCoordinate(resolvePath(row, view.longitude_field));
    if (latitude == null || longitude == null) continue;
    if (!inRange(latitude, longitude)) continue;
    const rawId = idField ? resolvePath(row, idField) : undefined;
    points.push({
      id: String(rawId ?? `${idOffset + index}`),
      latitude,
      longitude,
      label: view.label_field ? asLabel(resolvePath(row, view.label_field)) : null,
      timestamp: view.timestamp_field
        ? ((resolvePath(row, view.timestamp_field) as string | null) ?? null)
        : null,
      row,
    });
  }
  return points;
}

function mapTripRowsToPoints(
  rows: Record<string, unknown>[],
  locations: MobileArtifactLocation[],
  view: MobileFrontEndMapView,
  idField: string | null,
  idOffset: number,
): MapPoint[] {
  const points: MapPoint[] = [];
  for (const [index, row] of rows.entries()) {
    const rawId = idField ? resolvePath(row, idField) : undefined;
    const rowId = String(rawId ?? `${idOffset + index}`);
    for (const location of locations) {
      const pair = locationPairOnRow(row, location);
      if (!pair) continue;
      points.push({
        id: `${rowId}:${location.name}`,
        latitude: pair.latitude,
        longitude: pair.longitude,
        label: locationLabel(location.name),
        timestamp: view.timestamp_field
          ? ((resolvePath(row, view.timestamp_field) as string | null) ?? null)
          : null,
        row,
      });
    }
  }
  return points;
}
